
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, Sparkles, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface LeadsSearchBarProps {
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  clearSearch: () => void;
  onAISearch?: (query: string) => void;
  isAISearching?: boolean;
  isAIActive?: boolean;
  onClearAI?: () => void;
}

const LeadsSearchBar: React.FC<LeadsSearchBarProps> = ({
  searchQuery,
  setSearchQuery,
  clearSearch,
  onAISearch,
  isAISearching,
  isAIActive,
  onClearAI,
}) => {
  const [aiMode, setAiMode] = useState(false);
  const [aiQuery, setAiQuery] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (aiMode && onAISearch && aiQuery.trim()) {
      onAISearch(aiQuery.trim());
    }
  };

  const handleClearAI = () => {
    setAiQuery("");
    onClearAI?.();
  };

  const toggleAIMode = () => {
    const next = !aiMode;
    setAiMode(next);
    if (!next) {
      handleClearAI();
    } else {
      clearSearch();
    }
  };

  return (
    <form
      className="mb-8"
      onSubmit={handleSubmit}
      role="search"
      aria-label="Search leads"
      autoComplete="off"
    >
      <div className="relative max-w-2xl mx-auto flex gap-2 items-center">
        <div className="relative flex-1">
          {aiMode ? (
            <Sparkles className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary pointer-events-none" />
          ) : (
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
          )}
          {aiMode ? (
            <Input
              type="text"
              placeholder="Ask AI: e.g. 'restaurants in Dublin with high ratings'..."
              value={aiQuery}
              onChange={e => setAiQuery(e.target.value)}
              className={cn(
                "h-12 pl-12 pr-12 rounded-xl shadow-sm focus-visible:shadow-lg transition-shadow",
                "border-primary/40 ring-1 ring-primary/20"
              )}
              disabled={isAISearching}
            />
          ) : (
            <Input
              type="search"
              placeholder="Search by lead name, category, or location..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="h-12 pl-12 pr-12 rounded-xl border-border/60 shadow-sm focus-visible:shadow-lg transition-shadow"
            />
          )}
          {(aiMode ? aiQuery : searchQuery) && !isAISearching && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 hover:bg-muted/80 transition-all hover:rotate-90"
              onClick={aiMode ? handleClearAI : clearSearch}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          {isAISearching && (
            <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-primary" />
          )}
        </div>

        {/* AI mode toggle */}
        <Button
          type={aiMode ? "submit" : "button"}
          variant={aiMode ? "default" : "outline"}
          size="sm"
          onClick={aiMode ? undefined : toggleAIMode}
          disabled={aiMode && (!aiQuery.trim() || isAISearching)}
          className={cn(
            "h-12 px-4 rounded-xl gap-2 transition-all shrink-0",
            aiMode && "shadow-md"
          )}
        >
          <Sparkles className={cn("h-4 w-4", aiMode && "text-primary-foreground")} />
          <span className="hidden sm:inline text-xs font-medium">
            {aiMode ? "Search" : "AI"}
          </span>
        </Button>

        {aiMode && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={toggleAIMode}
            className="h-12 px-3 rounded-xl text-xs"
          >
            Cancel
          </Button>
        )}
      </div>

      {isAIActive && !isAISearching && (
        <div className="flex items-center justify-center mt-3 gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary bg-primary/10 px-3 py-1.5 rounded-full">
            <Sparkles className="h-3 w-3" />
            AI Search Active
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClearAI}
            className="h-7 text-xs text-muted-foreground hover:text-foreground"
          >
            Clear
          </Button>
        </div>
      )}
    </form>
  );
};

export default LeadsSearchBar;
